/**
 * LoadingScreen - Shows while world generates chunks
 *
 * Minecraft-style dirt background with progress bar, rotating tips,
 * and smooth fade-out transition.
 */
export class LoadingScreen {
  constructor() {
    this._tips = [
      'Los creepers le tienen miedo a los gatos',
      'Mina diamantes con pico de hierro o mejor',
      'Las antorchas evitan que aparezcan mobs hostiles',
      'Los lobos se domestican con huesos',
      'Puedes encantar herramientas en la mesa de encantamientos',
      'Las camas establecen tu punto de reaparicion',
      'El Enderman se enfurece si lo miras a los ojos',
      'Los slimes grandes se dividen al morir',
      'Presiona F2 para capturar una imagen',
      'Usa el arco para atacar desde lejos',
      'Presiona M para ver el minimapa',
      'Presiona R para ver el libro de recetas',
      'Los aldeanos comercian por esmeraldas',
      'Puedes cocinar comida en el horno',
      'Presiona Ctrl para correr mas rapido',
      'El obsidiana solo se puede minar con pico de diamante',
      'Los zombies pueden romper puertas en dificultad dificil',
      'Las aranas pueden escalar paredes',
      'Presiona T para abrir la terminal de comandos',
      'Puedes comerciar con aldeanos usando esmeraldas',
      'Las pociones negativas se lanzan como proyectiles',
      'Los esqueletos son mas peligrosos de lejos',
      'Presiona E para abrir tu inventario',
      'Los cofres guardan objetos de forma permanente',
      'El Nether tiene coordenadas 1:8 respecto al Overworld',
      'El Dragon del End tiene 200 puntos de vida',
      'Puedes encantar herramientas para hacerlas mas poderosas',
      'El trigo se cultiva en tierra de cultivo hidratada',
      'Las esmeraldas son mas raras que los diamantes',
      'Presiona C para abrir el crafteo rapido',
    ];
    this._tipIndex = Math.floor(Math.random() * this._tips.length);
    this._tipInterval = null;
    this._build();
    this._startTipRotation();
  }

  _build() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'loading-screen';
    this.overlay.style.cssText = `
      position: fixed; inset: 0;
      background: #3C2415;
      z-index: 9999;
      display: flex; flex-direction: column;
      justify-content: center; align-items: center;
      font-family: 'Courier New', monospace;
      image-rendering: pixelated;
      transition: opacity 0.5s;
    `;

    // Dirt pattern background (CSS checkerboard)
    this.overlay.style.backgroundImage = `
      repeating-conic-gradient(#3C2415 0% 25%, #4A2E1A 0% 50%) 0 0 / 32px 32px
    `;

    // Background video (cave exploration loop)
    this._bgVideo = document.createElement('video');
    this._bgVideo.style.cssText = `
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      opacity: 0.4;
      pointer-events: none;
    `;
    this._bgVideo.loop = true;
    this._bgVideo.muted = true;
    this._bgVideo.playsInline = true;
    this._bgVideo.preload = 'auto';
    fetch('/cave.webm', { method: 'HEAD' })
      .then(r => {
        if (r.ok && r.headers.get('content-type')?.startsWith('video/')) {
          this._bgVideo.src = '/cave.webm';
          this._bgVideo.play().catch(() => {});
        }
      })
      .catch(() => {});
    this.overlay.appendChild(this._bgVideo);

    // Title
    const title = document.createElement('div');
    title.textContent = 'FEDEWORLD';
    title.style.cssText = `
      font-size: 32px; font-weight: bold;
      color: #FFFFFF;
      text-shadow: 3px 3px 0 #3A3A3A;
      margin-bottom: 30px;
      letter-spacing: 4px;
    `;
    this.overlay.appendChild(title);

    // Status text
    this.statusText = document.createElement('div');
    this.statusText.textContent = 'Generando mundo...';
    this.statusText.style.cssText = `
      font-size: 14px;
      color: #CCCCCC;
      margin-bottom: 12px;
    `;
    this.overlay.appendChild(this.statusText);

    // Progress bar container
    const barWrap = document.createElement('div');
    barWrap.style.cssText = `
      width: 300px; height: 12px;
      background: #222222;
      border: 2px solid #555555;
      position: relative;
      border-radius: 2px;
      overflow: hidden;
    `;

    this.progressBar = document.createElement('div');
    this.progressBar.style.cssText = `
      height: 100%;
      background: linear-gradient(to bottom, #66CC44, #44AA22);
      width: 0%;
      transition: width 0.3s ease-out;
      position: relative;
    `;

    // Shine effect on progress bar
    const shine = document.createElement('div');
    shine.style.cssText = `
      position: absolute; top: 0; left: 0; right: 0;
      height: 50%;
      background: linear-gradient(to bottom, rgba(255,255,255,0.2), transparent);
      pointer-events: none;
    `;
    this.progressBar.appendChild(shine);

    barWrap.appendChild(this.progressBar);
    this.overlay.appendChild(barWrap);

    // Percentage text
    this.percentText = document.createElement('div');
    this.percentText.textContent = '0%';
    this.percentText.style.cssText = `
      font-size: 12px;
      color: #AAAAAA;
      margin-top: 6px;
      letter-spacing: 1px;
    `;
    this.overlay.appendChild(this.percentText);

    // Tip (rotating)
    this.tipEl = document.createElement('div');
    this.tipEl.style.cssText = `
      font-size: 12px;
      color: #999999;
      margin-top: 24px;
      max-width: 400px;
      text-align: center;
      min-height: 32px;
      transition: opacity 0.4s;
      line-height: 1.4;
    `;
    this.tipEl.textContent = this._tips[this._tipIndex];
    this.overlay.appendChild(this.tipEl);

    document.body.appendChild(this.overlay);
  }

  setProgress(fraction, text) {
    const pct = Math.min(100, fraction * 100);
    this.progressBar.style.width = `${pct}%`;
    this.percentText.textContent = `${Math.round(pct)}%`;
    if (text) this.statusText.textContent = text;
  }

  hide() {
    // Smooth fade-out
    this.overlay.style.opacity = '0';
    this.overlay.style.pointerEvents = 'none';
    if (this._bgVideo) this._bgVideo.pause();
    this._stopTipRotation();
    setTimeout(() => {
      this.overlay.style.display = 'none';
    }, 500);
  }

  show() {
    this.overlay.style.display = 'flex';
    this.overlay.style.opacity = '1';
    this.overlay.style.pointerEvents = 'auto';
    this.progressBar.style.width = '0%';
    this.percentText.textContent = '0%';
    this.statusText.textContent = 'Generando mundo...';
    this._startTipRotation();
  }

  /** Start rotating tips every 4 seconds */
  _startTipRotation() {
    this._stopTipRotation();
    this._tipInterval = setInterval(() => {
      // Fade out
      this.tipEl.style.opacity = '0';
      setTimeout(() => {
        this._tipIndex = (this._tipIndex + 1) % this._tips.length;
        this.tipEl.textContent = this._tips[this._tipIndex];
        this.tipEl.style.opacity = '1';
      }, 400);
    }, 4000);
  }

  /** Stop rotating tips */
  _stopTipRotation() {
    if (this._tipInterval) {
      clearInterval(this._tipInterval);
      this._tipInterval = null;
    }
  }
}
